import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useMobile } from '../utils/useMobile';
import { triggerHaptic, openExternalUrl } from '../utils/nativeBridge';

const CLAIM_CONFIG = {
  misleading: {
    labelEn: 'Misleading',
    labelAr: 'مضلل',
    badgeClass: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-500/30',
    borderClass: 'border-l-amber-500 rtl:border-r-amber-500',
    icon: 'fa-triangle-exclamation',
  },
  unverified: {
    labelEn: 'Unverified',
    labelAr: 'غير مؤكد',
    badgeClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/60 dark:text-yellow-300 border-yellow-500/30',
    borderClass: 'border-l-yellow-500 rtl:border-r-yellow-500',
    icon: 'fa-circle-question',
  },
  scam_alert: {
    labelEn: 'Scam Alert',
    labelAr: 'تحذير من احتيال',
    badgeClass: 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-500/30',
    borderClass: 'border-l-rose-500 rtl:border-r-rose-500',
    icon: 'fa-shield-halved',
  },
  verified_true: {
    labelEn: 'Verified Fact',
    labelAr: 'سياق مثبت',
    badgeClass: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-500/30',
    borderClass: 'border-l-emerald-500 rtl:border-r-emerald-500',
    icon: 'fa-circle-check',
  },
  out_of_context: {
    labelEn: 'Missing Context',
    labelAr: 'خارج السياق',
    badgeClass: 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border-sky-500/30',
    borderClass: 'border-l-sky-500 rtl:border-r-sky-500',
    icon: 'fa-info-circle',
  },
};

const TahaqqoqNoteCard = ({ postId, isPostAuthor = false }) => {
  const { t, i18n } = useTranslation();
  const isArabic = (i18n.language || '').toLowerCase().startsWith('ar');
  const { user } = useAuth();
  const toast = useToast();
  const isMobile = useMobile();

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeNote, setActiveNote] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Add Note Form State
  const [claimStatus, setClaimStatus] = useState('misleading');
  const [summary, setSummary] = useState('');
  const [citationUrl, setCitationUrl] = useState('');
  const [citationTitle, setCitationTitle] = useState('');
  const [citationsList, setCitationsList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchNotes = () => {
    if (!postId) return;
    axios
      .get(`/api/tahaqqoq/post/${postId}`)
      .then((res) => {
        setNotes(res.data.notes || []);
        setActiveNote(res.data.consensusNote || res.data.notes?.[0] || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchNotes();
  }, [postId]);

  const handleVote = async (noteId, voteType) => {
    if (!user) {
      toast.show(t('auth.loginRequired', 'Please sign in to vote'), 'info');
      return;
    }
    triggerHaptic('light');
    try {
      const res = await axios.post(`/api/tahaqqoq/note/${noteId}/vote`, { vote_type: voteType });
      toast.show(t('tahaqqoq.voteRecorded', 'Thank you for your feedback!'), 'success');
      setActiveNote(res.data.note);
      fetchNotes();
    } catch (err) {
      const msg = err.response?.data?.error || t('tahaqqoq.voteFailed', 'Vote could not be recorded');
      toast.show(msg, 'error');
    }
  };

  const handleAddCitation = () => {
    if (!citationUrl.trim()) return;
    triggerHaptic('selection');
    setCitationsList((prev) => [
      ...prev,
      { url: citationUrl.trim(), title: citationTitle.trim() || citationUrl.trim() },
    ]);
    setCitationUrl('');
    setCitationTitle('');
  };

  const handleSubmitNote = async (e) => {
    e.preventDefault();
    if (!summary.trim()) {
      toast.show(t('tahaqqoq.summaryRequired', 'Please provide a context summary'), 'error');
      return;
    }
    setSubmitting(true);
    triggerHaptic('medium');
    try {
      await axios.post(`/api/tahaqqoq/post/${postId}`, {
        claim_status: claimStatus,
        summary: summary.trim(),
        citations: citationsList,
      });
      triggerHaptic('success');
      toast.show(t('tahaqqoq.noteSubmitted', 'Context note submitted for community review!'), 'success');
      setShowAddModal(false);
      setSummary('');
      setCitationsList([]);
      fetchNotes();
    } catch (err) {
      const msg = err.response?.data?.error || t('tahaqqoq.submitFailed', 'Failed to submit context note');
      toast.show(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !activeNote) return null;

  const currentClaim = activeNote ? CLAIM_CONFIG[activeNote.claim_status] || CLAIM_CONFIG.misleading : null;

  return (
    <div className="w-full mt-3 mb-2" onClick={(e) => e.stopPropagation()}>
      {activeNote ? (
        <div
          className={`rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/80 dark:bg-zinc-900/80 p-3.5 sm:p-4 text-xs select-none shadow-xs border-l-4 rtl:border-l-0 rtl:border-r-4 ${
            currentClaim?.borderClass || 'border-l-indigo-500 rtl:border-r-indigo-500'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 font-bold text-zinc-800 dark:text-zinc-200 text-xs">
                <i className="fas fa-feather-pointed text-indigo-500 text-xs" />
                <span>{t('tahaqqoq.communityContext', isArabic ? 'سياق مجتمعي' : 'Community Context')}</span>
              </span>
              {activeNote.status === 'consensus_reached' && (
                <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-full border border-emerald-500/20">
                  Consensus ✓
                </span>
              )}
            </div>

            {/* Claim Tag */}
            {currentClaim && (
              <span
                className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${
                  currentClaim.badgeClass
                }`}
              >
                <i className={`fas ${currentClaim.icon} text-[10px]`} />
                <span>{t(`tahaqqoq.claims.${activeNote.claim_status}`, isArabic ? currentClaim.labelAr : currentClaim.labelEn)}</span>
              </span>
            )}
          </div>

          {/* Context Summary */}
          <p className="text-[13px] text-zinc-800 dark:text-zinc-200 leading-relaxed font-normal mb-2.5">
            {activeNote.summary}
          </p>

          {/* Citations & Sources */}
          {Array.isArray(activeNote.citations) && activeNote.citations.length > 0 && (
            <div className="mb-3 pt-2 border-t border-zinc-200/60 dark:border-zinc-800/80">
              <div className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 mb-1.5 flex items-center gap-1">
                <i className="fas fa-link text-[10px]" />
                <span>{t('tahaqqoq.sources', 'Sources & Official Citations')}:</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {activeNote.citations.map((cite, i) => (
                  <a
                    key={i}
                    href={cite.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      openExternalUrl(cite.url);
                    }}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline bg-white dark:bg-zinc-800 px-2.5 py-1 rounded-full border border-zinc-200 dark:border-zinc-700 max-w-[220px] truncate touch-press"
                  >
                    <i className="fas fa-external-link-alt text-[9px] shrink-0" />
                    <span className="truncate">{cite.title || cite.url}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Voting / Feedback footer */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-2 border-t border-zinc-200/60 dark:border-zinc-800/80 text-[11px] text-zinc-500 dark:text-zinc-400">
            <span>{t('tahaqqoq.wasHelpful', 'Do you find this context helpful?')}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleVote(activeNote.id, 'helpful')}
                className={`flex-1 sm:flex-initial px-3 py-1.5 rounded-full font-bold transition-all touch-press ${
                  activeNote.viewer_vote === 'helpful'
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'bg-white dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700'
                }`}
              >
                <i className="fas fa-thumbs-up mr-1 text-[10px]" />
                {t('tahaqqoq.yesHelpful', 'Helpful')} ({activeNote.helpful_count || 0})
              </button>
              <button
                type="button"
                onClick={() => handleVote(activeNote.id, 'not_helpful')}
                className={`flex-1 sm:flex-initial px-3 py-1.5 rounded-full font-bold transition-all touch-press ${
                  activeNote.viewer_vote === 'not_helpful'
                    ? 'bg-rose-600 text-white shadow-xs'
                    : 'bg-white dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700'
                }`}
              >
                <i className="fas fa-thumbs-down mr-1 text-[10px]" />
                {t('tahaqqoq.notHelpful', 'Not helpful')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Action to propose context if no active note */
        !isPostAuthor && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                triggerHaptic('light');
                setShowAddModal(true);
              }}
              className="text-[11px] font-bold text-zinc-400 hover:text-indigo-600 dark:hover:text-indigo-400 inline-flex items-center gap-1.5 transition-colors py-1 touch-press"
            >
              <i className="fas fa-feather-pointed text-[10px] text-indigo-500" />
              <span>{t('tahaqqoq.addContext', isArabic ? 'إضافة سياق مجتمعي' : 'Add Community Context')}</span>
            </button>
          </div>
        )
      )}

      {/* Propose Note Modal */}
      {showAddModal && (
        <div
          className={`fixed inset-0 z-50 flex justify-center bg-black/60 backdrop-blur-xs transition-opacity duration-200 ${
            isMobile ? 'items-end p-0' : 'items-center p-4'
          }`}
          onClick={() => setShowAddModal(false)}
        >
          <div
            className={`w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl overflow-hidden transition-all duration-300 ${
              isMobile
                ? 'rounded-t-[32px] border-b-0 max-h-[92vh] flex flex-col pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))] animate-in slide-in-from-bottom-8'
                : 'max-w-lg rounded-3xl p-6 animate-in zoom-in-95'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {isMobile && (
              <div className="pt-3 pb-1 flex justify-center">
                <div className="w-12 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full" />
              </div>
            )}

            <div className={`overflow-y-auto no-scrollbar ${isMobile ? 'px-5 py-3' : ''}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-black text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                  <i className="fas fa-feather-pointed text-indigo-500" />
                  <span>{t('tahaqqoq.writeContextNote', isArabic ? 'كتابة توضيح مجتمعي' : 'Write Community Note')}</span>
                </h3>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors touch-press"
                >
                  <i className="fas fa-times text-sm" />
                </button>
              </div>

              <form onSubmit={handleSubmitNote} className="space-y-4">
                {/* Claim Classification */}
                <div>
                  <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                    {t('tahaqqoq.claimClassification', isArabic ? 'تقييم المعلومة' : 'Fact Assessment')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(CLAIM_CONFIG).map(([key, config]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          triggerHaptic('selection');
                          setClaimStatus(key);
                        }}
                        className={`p-2 rounded-xl text-left border text-xs font-bold transition-all touch-press ${
                          claimStatus === key
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300 shadow-xs'
                            : 'border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300'
                        }`}
                      >
                        <i className={`fas ${config.icon} mr-1.5 text-[11px]`} />
                        <span>{t(`tahaqqoq.claims.${key}`, isArabic ? config.labelAr : config.labelEn)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Summary Input */}
                <div>
                  <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                    {t('tahaqqoq.contextExplanation', isArabic ? 'توضيح السياق' : 'Context Explanation')}
                  </label>
                  <textarea
                    rows="3"
                    placeholder={t('tahaqqoq.summaryPlaceholder', 'Provide clear context and factual counter-evidence...')}
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    maxLength={500}
                    className="w-full p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-800/80 border border-zinc-200 dark:border-zinc-700 text-xs sm:text-sm text-zinc-900 dark:text-zinc-100 outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  />
                </div>

                {/* Citations Input */}
                <div>
                  <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                    {t('tahaqqoq.attachSource', 'Attach Reliable Source / Link')}
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="url"
                      placeholder="https://..."
                      value={citationUrl}
                      onChange={(e) => setCitationUrl(e.target.value)}
                      className="flex-1 h-10 px-3 rounded-xl bg-zinc-50 dark:bg-zinc-800/80 border border-zinc-200 dark:border-zinc-700 text-xs text-zinc-900 dark:text-zinc-100 outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <input
                      type="text"
                      placeholder="Source (e.g. MAP, Hespress)"
                      value={citationTitle}
                      onChange={(e) => setCitationTitle(e.target.value)}
                      className="w-1/3 h-10 px-3 rounded-xl bg-zinc-50 dark:bg-zinc-800/80 border border-zinc-200 dark:border-zinc-700 text-xs text-zinc-900 dark:text-zinc-100 outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={handleAddCitation}
                      className="px-3.5 h-10 rounded-xl bg-zinc-200 dark:bg-zinc-700 text-xs font-bold text-zinc-800 dark:text-zinc-200 hover:bg-zinc-300 touch-press"
                    >
                      Add
                    </button>
                  </div>

                  {citationsList.length > 0 && (
                    <div className="space-y-1">
                      {citationsList.map((c, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between text-[11px] bg-zinc-100 dark:bg-zinc-800 px-3 py-1.5 rounded-xl border border-zinc-200 dark:border-zinc-700"
                        >
                          <span className="truncate max-w-[80%] font-medium text-zinc-700 dark:text-zinc-300">
                            {c.title} ({c.url})
                          </span>
                          <button
                            type="button"
                            onClick={() => setCitationsList(citationsList.filter((_, i) => i !== idx))}
                            className="text-rose-500 hover:text-rose-700 touch-press"
                          >
                            <i className="fas fa-times text-xs" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2.5 rounded-xl text-xs font-bold text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 touch-press"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-md shadow-indigo-600/20 touch-press"
                  >
                    {submitting ? <i className="fas fa-spinner fa-spin" /> : t('tahaqqoq.submitNote', isArabic ? 'إرسال التوثيق' : 'Submit Note')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TahaqqoqNoteCard;
